import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Loader2, Upload, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import type { Condominio } from '@/lib/index';
import { useAuth } from '@/hooks/useAuth';
import { useCondomini } from '@/hooks/useCondomini';
import { supabase } from '@/integrations/supabase/client';

const loginSchema = z.object({
  email: z.string().email('Email non valida'),
  password: z.string().min(6, 'La password deve contenere almeno 6 caratteri'),
});

type LoginFormData = z.infer<typeof loginSchema>;

interface LoginFormProps {
  onSubmit: (email: string, password: string) => Promise<void>;
}

export function LoginForm({ onSubmit }: LoginFormProps) {
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  const handleFormSubmit = async (data: LoginFormData) => {
    try {
      setLoading(true);
      setError(null);
      await onSubmit(data.email, data.password);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Errore durante il login';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
      <div className="space-y-2">
        <Label htmlFor="email">Email</Label>
        <Input
          id="email"
          type="email"
          placeholder="tuo@email.it"
          {...register('email')}
          disabled={loading}
        />
        {errors.email && (
          <p className="text-sm text-destructive">{errors.email.message}</p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="password">Password</Label>
        <Input
          id="password"
          type="password"
          placeholder="••••••••"
          {...register('password')}
          disabled={loading}
        />
        {errors.password && (
          <p className="text-sm text-destructive">{errors.password.message}</p>
        )}
      </div>

      {error && (
        <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/20">
          <p className="text-sm text-destructive">{error}</p>
        </div>
      )}

      <Button type="submit" className="w-full" disabled={loading}>
        {loading ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Accesso in corso...
          </>
        ) : (
          'Accedi'
        )}
      </Button>
    </form>
  );
}

const condominioSchema = z.object({
  nome: z.string().min(2, 'Il nome deve contenere almeno 2 caratteri'),
  indirizzo: z.string().optional(),
  cf: z.string().optional(),
  iban: z.string().optional(),
  amministratore: z.string().optional(),
  anno_costruzione: z.coerce.number().optional(),
  num_unita: z.coerce.number().optional(),
  // Anagrafica estesa
  assicurazione_compagnia: z.string().optional(),
  assicurazione_polizza: z.string().optional(),
  ascensore_ditta: z.string().optional(),
  ascensore_matricola: z.string().optional(),
  impianti_termici_ditta: z.string().optional(),
  verifica_elevatori: z.string().optional(),
  verifica_acqua: z.string().optional(),
  note: z.string().optional(),
});

type CondominioFormData = z.infer<typeof condominioSchema>;

interface CondominioFormProps {
  onSubmit: (data: CondominioFormData) => Promise<void>;
  initialData?: Partial<Condominio>;
  onCancel: () => void;
}

export function CondominioForm({
  onSubmit,
  initialData,
  onCancel,
}: CondominioFormProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<CondominioFormData>({
    resolver: zodResolver(condominioSchema),
    defaultValues: initialData
      ? {
          nome: initialData.nome || '',
          indirizzo: initialData.indirizzo || '',
          cf: initialData.cf || '',
          iban: initialData.iban || '',
          amministratore: initialData.amministratore || '',
          anno_costruzione: initialData.anno_costruzione,
          num_unita: initialData.num_unita,
          assicurazione_compagnia: initialData.assicurazione_compagnia || '',
          assicurazione_polizza: initialData.assicurazione_polizza || '',
          ascensore_ditta: initialData.ascensore_ditta || '',
          ascensore_matricola: initialData.ascensore_matricola || '',
          impianti_termici_ditta: initialData.impianti_termici_ditta || '',
          verifica_elevatori: initialData.verifica_elevatori || '',
          verifica_acqua: initialData.verifica_acqua || '',
          note: initialData.note || '',
        }
      : undefined,
  });

  const handleFormSubmit = async (data: CondominioFormData) => {
    try {
      setLoading(true);
      setError(null);
      await onSubmit(data);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Errore salvataggio condominio';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="nome">Nome condominio *</Label>
        <Input
          id="nome"
          placeholder="Condominio Rossi"
          {...register('nome')}
          disabled={loading}
        />
        {errors.nome && (
          <p className="text-sm text-destructive">{errors.nome.message}</p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="indirizzo">Indirizzo</Label>
        <Input
          id="indirizzo"
          placeholder="Via Roma 123"
          {...register('indirizzo')}
          disabled={loading}
        />
        {errors.indirizzo && (
          <p className="text-sm text-destructive">{errors.indirizzo.message}</p>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="cf">Codice Fiscale</Label>
          <Input
            id="cf"
            placeholder="CF condominio"
            {...register('cf')}
            disabled={loading}
          />
          {errors.cf && (
            <p className="text-sm text-destructive">{errors.cf.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="iban">IBAN</Label>
          <Input
            id="iban"
            placeholder="IT00X0000000000000000000000"
            {...register('iban')}
            disabled={loading}
          />
          {errors.iban && (
            <p className="text-sm text-destructive">{errors.iban.message}</p>
          )}
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="amministratore">Amministratore</Label>
        <Input
          id="amministratore"
          placeholder="Nome amministratore"
          {...register('amministratore')}
          disabled={loading}
        />
        {errors.amministratore && (
          <p className="text-sm text-destructive">{errors.amministratore.message}</p>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="anno_costruzione">Anno costruzione</Label>
          <Input
            id="anno_costruzione"
            type="number"
            placeholder="1990"
            {...register('anno_costruzione')}
            disabled={loading}
          />
          {errors.anno_costruzione && (
            <p className="text-sm text-destructive">{errors.anno_costruzione.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="num_unita">Numero unità</Label>
          <Input
            id="num_unita"
            type="number"
            placeholder="12"
            {...register('num_unita')}
            disabled={loading}
          />
          {errors.num_unita && (
            <p className="text-sm text-destructive">{errors.num_unita.message}</p>
          )}
        </div>
      </div>

      {/* ─── SEZIONE ASSICURAZIONE ─── */}
      <div className="border-t pt-4 mt-2">
        <p className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">🛡️ Assicurazione</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="assicurazione_compagnia">Compagnia assicurativa</Label>
            <Input
              id="assicurazione_compagnia"
              placeholder="Es. Generali, Allianz..."
              {...register('assicurazione_compagnia')}
              disabled={loading}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="assicurazione_polizza">Numero di polizza</Label>
            <Input
              id="assicurazione_polizza"
              placeholder="Es. 12345678"
              {...register('assicurazione_polizza')}
              disabled={loading}
            />
          </div>
        </div>
      </div>

      {/* ─── SEZIONE ASCENSORE ─── */}
      <div className="border-t pt-4">
        <p className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">🛗 Manutenzione Ascensore</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="ascensore_ditta">Ditta di manutenzione</Label>
            <Input
              id="ascensore_ditta"
              placeholder="Es. Otis, Kone..."
              {...register('ascensore_ditta')}
              disabled={loading}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="ascensore_matricola">Matricola impianto/i</Label>
            <Input
              id="ascensore_matricola"
              placeholder="Es. MI12345 / MI67890"
              {...register('ascensore_matricola')}
              disabled={loading}
            />
          </div>
        </div>
      </div>

      {/* ─── SEZIONE IMPIANTI TERMICI ─── */}
      <div className="border-t pt-4">
        <p className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">🔥 Impianti Termici</p>
        <div className="space-y-2">
          <Label htmlFor="impianti_termici_ditta">Ditta di manutenzione impianti termici</Label>
          <Input
            id="impianti_termici_ditta"
            placeholder="Es. Termoidraulica Rossi Srl"
            {...register('impianti_termici_ditta')}
            disabled={loading}
          />
        </div>
      </div>

      {/* ─── SEZIONE VERIFICHE ─── */}
      <div className="border-t pt-4">
        <p className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">✅ Verifiche Obbligatorie</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="verifica_elevatori">Verifica impianti elevatori</Label>
            <Input
              id="verifica_elevatori"
              placeholder="Es. INAIL / Organismo notificato"
              {...register('verifica_elevatori')}
              disabled={loading}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="verifica_acqua">Verifica acqua potabile</Label>
            <Input
              id="verifica_acqua"
              placeholder="Es. ARPA / ASL - data ultima verifica"
              {...register('verifica_acqua')}
              disabled={loading}
            />
          </div>
        </div>
      </div>

      {/* ─── NOTE ─── */}
      <div className="border-t pt-4">
        <p className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">📝 Note</p>
        <div className="space-y-2">
          <Label htmlFor="note">Note generali</Label>
          <textarea
            id="note"
            className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            placeholder="Note aggiuntive sul condominio..."
            {...register('note')}
            disabled={loading}
          />
        </div>
      </div>

      {error && (
        <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/20">
          <p className="text-sm text-destructive">{error}</p>
        </div>
      )}

      <div className="flex gap-2 justify-end">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={loading}
        >
          Annulla
        </Button>
        <Button type="submit" disabled={loading}>
          {loading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Salvataggio...
            </>
          ) : initialData ? (
            'Salva modifiche'
          ) : (
            'Crea condominio'
          )}
        </Button>
      </div>
    </form>
  );
}

const uploadDocumentoSchema = z.object({
  condominio_mode: z.enum(['existing', 'new']),
  condominio_id: z.string().optional(),
  nuovo_nome: z.string().optional(),
  tipo: z.enum(['consuntivo', 'preventivo'], {
    required_error: 'Seleziona il tipo di documento',
  }),
  anno: z.coerce.number().min(2018).max(2026),
}).superRefine((data, ctx) => {
  if (data.condominio_mode === 'existing' && !data.condominio_id) {
    ctx.addIssue({ code: 'custom', path: ['condominio_id'], message: 'Seleziona un condominio' });
  }
  if (data.condominio_mode === 'new') {
    if (!data.nuovo_nome || data.nuovo_nome.length < 2)
      ctx.addIssue({ code: 'custom', path: ['nuovo_nome'], message: 'Nome obbligatorio (min 2 caratteri)' });
  }
});

type UploadDocumentoFormData = z.infer<typeof uploadDocumentoSchema>;

interface UploadDocumentoFormProps {
  onSuccess: () => void;
}

export function UploadDocumentoForm({ onSuccess }: UploadDocumentoFormProps) {
  const [file, setFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [statusMsg, setStatusMsg] = useState('');
  const [uploading, setUploading] = useState(false);

  const { user: _authUser } = useAuth();
  const { condomini } = useCondomini();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<UploadDocumentoFormData>({
    resolver: zodResolver(uploadDocumentoSchema),
    defaultValues: { condominio_mode: 'existing', anno: 2026 },
  });

  const condominioMode = watch('condominio_mode');
  const selectedCondominioId = watch('condominio_id');
  const selectedTipo = watch('tipo');
  const selectedAnno = watch('anno');

  const MAX_FILE_SIZE_MB = 100;
  const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      if (selectedFile.type !== 'application/pdf') {
        setStatusMsg('✗ Solo file PDF sono supportati');
        setFile(null);
        e.target.value = '';
        return;
      }
      if (selectedFile.size > MAX_FILE_SIZE_BYTES) {
        setStatusMsg(`✗ File troppo grande: ${(selectedFile.size / 1024 / 1024).toFixed(1)} MB. Limite massimo: ${MAX_FILE_SIZE_MB} MB`);
        setFile(null);
        e.target.value = '';
        return;
      }
      setFile(selectedFile);
      setStatusMsg('');
    }
  };

  const onSubmit = async (data: UploadDocumentoFormData) => {
    if (!file) {
      setStatusMsg('✗ Seleziona un file PDF');
      return;
    }

    let progressInterval: ReturnType<typeof setInterval> | null = null;

    try {
      setUploading(true);
      setUploadProgress(5);
      setStatusMsg('Verifica sessione...');

      // Ottieni sessione fresca da Supabase
      let accessToken: string | null = null;
      const { data: { session: freshSession } } = await supabase.auth.getSession();
      if (freshSession?.access_token) {
        accessToken = freshSession.access_token;
      } else {
        const { data: refreshed } = await supabase.auth.refreshSession();
        if (refreshed?.session?.access_token) {
          accessToken = refreshed.session.access_token;
        } else {
          throw new Error('Sessione scaduta. Ricarica la pagina ed effettua nuovamente l\'accesso.');
        }
      }

      setUploadProgress(15);
      setStatusMsg('Inizializzazione...');

      let condominioId = data.condominio_id ?? '';
      let nomeNuovoCondominio: string | undefined;

      if (data.condominio_mode === 'new') {
        nomeNuovoCondominio = data.nuovo_nome!;
        setStatusMsg('Creazione condominio e caricamento...');
      }

      setStatusMsg('Caricamento PDF in corso...');
      setUploadProgress(25);

      progressInterval = setInterval(() => {
        setUploadProgress((prev) => (prev < 88 ? prev + 3 : prev));
      }, 500);

      // Usa edge function server-side che bypassa completamente le RLS
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const formData = new FormData();
      formData.append('file', file);
      formData.append('tipo', data.tipo);
      formData.append('anno', String(data.anno));
      if (condominioId) formData.append('condominio_id', condominioId);
      if (nomeNuovoCondominio) formData.append('nome_condominio', nomeNuovoCondominio);

      const response = await fetch(
        `${supabaseUrl}/functions/v1/upload-documento-2026-05-10`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          body: formData,
        }
      );

      const result = await response.json() as Record<string, unknown>;

      if (!response.ok || result.error) {
        const errMsg = (result.error as string) || `Errore HTTP ${response.status}`;
        throw new Error(errMsg);
      }

      if (progressInterval) {
        clearInterval(progressInterval);
        progressInterval = null;
      }

      setUploadProgress(100);
      setStatusMsg('✓ Documento caricato con successo!');

      setTimeout(() => {
        reset();
        setFile(null);
        setUploadProgress(0);
        setStatusMsg('');
        onSuccess();
      }, 800);

    } catch (error) {
      if (progressInterval) {
        clearInterval(progressInterval);
        progressInterval = null;
      }
      let msg = 'Errore sconosciuto durante il caricamento';
      if (error instanceof Error) {
        msg = error.message;
      } else if (error && typeof error === 'object') {
        const errObj = error as Record<string, unknown>;
        msg = (errObj.message as string) || (errObj.error as string) || JSON.stringify(error);
      } else if (typeof error === 'string') {
        msg = error;
      }
      setStatusMsg(`✗ ${msg}`);
      setUploadProgress(0);
    } finally {
      setUploading(false);
    }
  };

  const years = Array.from({ length: 9 }, (_, i) => 2026 - i);

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
      <div className="space-y-2">
        <Label>Condominio *</Label>
        <div className="flex gap-2">
          <Button
            type="button"
            size="sm"
            variant={condominioMode === 'existing' ? 'default' : 'outline'}
            onClick={() => setValue('condominio_mode', 'existing')}
            disabled={uploading}
            className="flex-1"
          >
            Esistente
          </Button>
          <Button
            type="button"
            size="sm"
            variant={condominioMode === 'new' ? 'default' : 'outline'}
            onClick={() => setValue('condominio_mode', 'new')}
            disabled={uploading}
            className="flex-1 gap-1"
          >
            + Nuovo condominio
          </Button>
        </div>
      </div>

      {condominioMode === 'existing' && (
        <div className="space-y-2">
          {condomini.length === 0 ? (
            <div className="rounded-lg border border-warning/40 bg-warning/10 p-3 text-sm text-warning">
              ⚠️ Nessun condominio trovato. Crea prima un condominio dalla sezione Condomini,
              oppure usa il pulsante "+ Nuovo condominio" qui sopra.
            </div>
          ) : (
            <Select
              value={selectedCondominioId || ''}
              onValueChange={(value) => setValue('condominio_id', value)}
              disabled={uploading}
            >
              <SelectTrigger className={errors.condominio_id ? 'border-destructive' : ''}>
                <SelectValue placeholder={`Seleziona condominio (${condomini.length} disponibili)`} />
              </SelectTrigger>
              <SelectContent className="max-h-60 overflow-y-auto">
                {condomini.map((cond) => (
                  <SelectItem key={cond.id} value={cond.id}>
                    <span className="font-medium">{cond.nome}</span>
                    {cond.indirizzo && (
                      <span className="ml-2 text-muted-foreground text-xs">
                        {cond.indirizzo}
                      </span>
                    )}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {errors.condominio_id && (
            <p className="text-sm text-destructive">{errors.condominio_id.message}</p>
          )}
        </div>
      )}

      {condominioMode === 'new' && (
        <div className="rounded-lg border border-primary/30 bg-primary/5 p-4 space-y-3">
          <p className="text-xs font-semibold text-primary uppercase tracking-wide">Dati nuovo condominio</p>

          <div className="space-y-2">
            <Label htmlFor="nuovo_nome">Nome condominio *</Label>
            <Input
              id="nuovo_nome"
              placeholder="Es. Condominio Bellavista"
              {...register('nuovo_nome')}
              disabled={uploading}
            />
            {errors.nuovo_nome && (
              <p className="text-sm text-destructive">{errors.nuovo_nome.message}</p>
            )}
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Tipo documento *</Label>
          <Select
            value={selectedTipo}
            onValueChange={(value) => setValue('tipo', value as 'consuntivo' | 'preventivo')}
            disabled={uploading}
          >
            <SelectTrigger>
              <SelectValue placeholder="Seleziona tipo" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="consuntivo">Consuntivo</SelectItem>
              <SelectItem value="preventivo">Preventivo</SelectItem>
            </SelectContent>
          </Select>
          {errors.tipo && (
            <p className="text-sm text-destructive">{errors.tipo.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label>Anno *</Label>
          <Select
            value={selectedAnno?.toString()}
            onValueChange={(value) => setValue('anno', parseInt(value))}
            disabled={uploading}
          >
            <SelectTrigger>
              <SelectValue placeholder="Anno" />
            </SelectTrigger>
            <SelectContent>
              {years.map((year) => (
                <SelectItem key={year} value={year.toString()}>
                  {year}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.anno && (
            <p className="text-sm text-destructive">{errors.anno.message}</p>
          )}
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="file">File PDF *</Label>
        <div className="flex items-center gap-2">
          <Input
            id="file"
            type="file"
            accept=".pdf,application/pdf"
            onChange={handleFileChange}
            disabled={uploading}
            className="flex-1"
          />
          {file && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => setFile(null)}
              disabled={uploading}
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
        <p className="text-xs text-muted-foreground">📎 Solo PDF · Dimensione massima: 100 MB</p>
        {file && (
          <p className="text-sm text-muted-foreground">
            📄 {file.name} — <span className={file.size > 50 * 1024 * 1024 ? 'text-warning font-medium' : ''}>{(file.size / 1024 / 1024).toFixed(2)} MB</span>
          </p>
        )}
      </div>

      {uploadProgress > 0 && (
        <div className="space-y-1">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">{statusMsg}</span>
            <span className="font-medium">{uploadProgress}%</span>
          </div>
          <Progress value={uploadProgress} className="h-2" />
        </div>
      )}

      {statusMsg && uploadProgress === 0 && (
        <p className="text-sm text-destructive">{statusMsg}</p>
      )}

      <Button type="submit" disabled={uploading || !file} className="w-full">
        {uploading ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            {statusMsg || 'In corso...'}
          </>
        ) : (
          <>
            <Upload className="mr-2 h-4 w-4" />
            {condominioMode === 'new' ? 'Crea e Carica' : 'Carica'}
          </>
        )}
      </Button>
    </form>
  );
}

const createUserSchema = z.object({
  email: z.string().email('Email non valida'),
  full_name: z.string().min(2, 'Il nome deve contenere almeno 2 caratteri'),
  password: z.string().optional().refine(
    (p) => !p || p.length === 0 || p.length >= 8,
    { message: 'La password deve contenere almeno 8 caratteri' }
  ),
});

type CreateUserFormData = z.infer<typeof createUserSchema>;

interface CreateUserFormProps {
  onSuccess: () => void;
}

export function CreateUserForm({ onSuccess }: CreateUserFormProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successInfo, setSuccessInfo] = useState<{ email: string; password: string; emailSent: boolean } | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<CreateUserFormData>({
    resolver: zodResolver(createUserSchema),
  });

  const onSubmit = async (data: CreateUserFormData) => {
    try {
      setLoading(true);
      setError(null);
      setSuccessInfo(null);

      const { data: result, error: functionError } = await supabase.functions.invoke('create-user', {
        body: {
          email: data.email,
          full_name: data.full_name,
          password: data.password,
        },
      });

      // Interpreta la risposta: result può contenere success anche se functionError è impostato
      const res = result as { success?: boolean; email?: string; password?: string; email_sent?: boolean; error?: string } | null;

      // Se abbiamo successo nel body, ignora l'errore HTTP
      if (res?.success) {
        setSuccessInfo({
          email: res.email || data.email,
          password: res.password || data.password || '',
          emailSent: res.email_sent || false,
        });
        reset();
        // Notifica il parent dopo 3 secondi così l'utente può leggere le credenziali
        setTimeout(() => onSuccess(), 3000);
        return;
      }

      // Altrimenti gestisci l'errore
      if (functionError) {
        const errMsg = res?.error ||
          (functionError as { message?: string })?.message ||
          'Errore durante la creazione utente';
        throw new Error(String(errMsg));
      }

      if (!res?.success) {
        throw new Error(res?.error || 'Creazione utente fallita');
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Errore creazione utente';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  // Schermata successo
  if (successInfo) {
    return (
      <div className="space-y-4">
        <div className="p-4 rounded-lg bg-green-50 border border-green-200">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center">
              <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <p className="font-semibold text-green-800">Utente creato con successo!</p>
          </div>
          <div className="space-y-2 text-sm">
            <div className="flex items-center gap-2">
              <span className="text-gray-500 w-20">Email:</span>
              <code className="bg-white border border-gray-200 px-2 py-0.5 rounded text-gray-800">{successInfo.email}</code>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-gray-500 w-20">Password:</span>
              <code className="bg-white border border-gray-200 px-2 py-0.5 rounded font-bold text-gray-900 text-base">{successInfo.password}</code>
            </div>
          </div>
          {successInfo.emailSent ? (
            <p className="mt-3 text-xs text-green-700">✉️ Credenziali inviate via email all&apos;utente.</p>
          ) : (
            <p className="mt-3 text-xs text-amber-700">⚠️ Email non inviata (RESEND non configurato). Comunicare le credenziali manualmente.</p>
          )}
        </div>
        <p className="text-xs text-center text-muted-foreground">Finestra chiusa automaticamente...</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="email">Email *</Label>
        <Input
          id="email"
          type="email"
          placeholder="utente@esempio.it"
          {...register('email')}
          disabled={loading}
        />
        {errors.email && (
          <p className="text-sm text-destructive">{errors.email.message}</p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="full_name">Nome completo *</Label>
        <Input
          id="full_name"
          placeholder="Mario Rossi"
          {...register('full_name')}
          disabled={loading}
        />
        {errors.full_name && (
          <p className="text-sm text-destructive">{errors.full_name.message}</p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="password">Password (opzionale — genera automatica se vuota)</Label>
        <Input
          id="password"
          type="password"
          placeholder="Lascia vuoto per generazione automatica"
          {...register('password')}
          disabled={loading}
        />
        <p className="text-xs text-muted-foreground">Min. 8 caratteri. Se lasciato vuoto viene generata automaticamente.</p>
        {errors.password && (
          <p className="text-sm text-destructive">{errors.password.message}</p>
        )}
      </div>

      {error && (
        <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/20">
          <p className="text-sm text-destructive font-medium">❌ {error}</p>
        </div>
      )}

      <Button type="submit" disabled={loading} className="w-full">
        {loading ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Creazione in corso...
          </>
        ) : (
          'Crea utente'
        )}
      </Button>
    </form>
  );
}